<template>
  <div class="flex gap-2 overflow-auto">
    <slot name="before" />
    <label
      v-for="option in options"
      :key="option.value"
      :tabindex="option.disabled ? -1 : 0"
      :class="['mb-0 rounded-full', option.disabled ? 'cursor-not-allowed' : 'cursor-pointer']"
      @keydown.enter.prevent="!option.disabled && toggleSelectionWithKey(option.value)"
      @keydown.space.prevent="!option.disabled && toggleSelectionWithKey(option.value)"
    >
      <input
        v-model="selectedValues"
        :type="allowMultipleSelection ? 'checkbox' : 'radio'"
        :name="name"
        :value="option.value"
        :disabled="!!option.disabled"
        :aria-checked="isSelected(option.value)"
        :aria-label="option.label"
        class="hidden"
        @change="emit('update:modelValue', selectedValues); emit('change', option.value, isSelected(option.value))"
      >
      <Tooltip :text="option.tooltip || ''" :disabled="!option.tooltip">
        <span
          role="button"
          class="inline-flex items-center px-4 py-2 border border-solid rounded-full font-normal whitespace-nowrap transition-all duration-200 ease-in-out"
          :class="[getChipClasses(option), additionalItemClasses]"
        >
          <component :is="option.iconComponent" v-if="option.iconComponent && iconPosition === 'left'" :value="option.value" :class="['w-auto h-4 mr-1.5', additionalIconClasses, option.additionalIconClasses]" />
          {{ option.label }}
          <component :is="option.iconComponent" v-if="option.iconComponent && iconPosition === 'right'" :value="option.value" :class="['w-auto h-4 ml-1.5', additionalIconClasses, option.additionalIconClasses]" />
        </span>
      </Tooltip>
    </label>
    <slot name="after" />
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';

import Tooltip from '@/components/Ui/Tooltip.vue';
import { generateUniqueId } from '@/services/helpers';

const emit = defineEmits(['update:modelValue', 'change']);

const props = defineProps({
  modelValue: {
    type: [Array, String, Number], // Array for multiple selection, String or Number for single selection
    default: () => [],
  },
  options: {
    type: Array,
    required: true,
  },
  name: {
    type: String,
    default: () => `input-${generateUniqueId()}`,
  },
  // Note: these classes will not override the default icon component classes
  additionalIconClasses: {
    type: String,
    default: '',
  },
  additionalItemClasses: {
    type: String,
    default: '',
  },
  iconPosition: {
    type: String,
    default: 'left',
    validator: (value) => ['left', 'right'].includes(value),
  },
});

const selectedValues = ref(props.modelValue);

watch(() => props.modelValue, (newValue) => {
  selectedValues.value = newValue;
});

const allowMultipleSelection = computed(() => Array.isArray(props.modelValue));

const isSelected = (value) => {
  return Array.isArray(selectedValues.value)
    ? selectedValues.value.includes(value)
    : selectedValues.value === value;
};

const toggleSelectionWithKey = (value) => {
  if (allowMultipleSelection.value) {
    selectedValues.value = selectedValues.value.includes(value)
      ? selectedValues.value.filter((v) => v !== value)
      : [...selectedValues.value, value];
  } else {
    selectedValues.value = selectedValues.value === value ? null : value;
  }
  // Keyboard selection must mirror the hidden input's @change so the parent
  // gets the same update:modelValue + change events a mouse click produces.
  emit('update:modelValue', selectedValues.value);
  emit('change', value, isSelected(value));
};

const getChipClasses = (option) => {
  if (option.disabled) {
    return isSelected(option.value)
      ? 'border-green-200 bg-green-100 text-grey-500 cursor-not-allowed'
      : 'border-grey-400 text-grey-500 cursor-not-allowed';
  }

  return isSelected(option.value)
    ? (option.selectedItemClasses ?? 'border-green bg-green-100 hover:bg-green-200')
    : (option.unselectedItemClasses ?? 'border-grey-400 text-grey-800 hover:bg-grey-100');
};
</script>
